import {
  findProduct,
  getStartupProductId,
} from "@/configurator/domain/logic/catalog";
import { getDefaultSelectedOptions } from "@/configurator/domain/logic/selection";
import type {
  ConfiguratorConfig,
  Id,
  OptionId,
  SelectedOptions,
  SharedSelection,
} from "@/configurator/domain/models/types";

function isSameSelection(
  value: OptionId | OptionId[],
  fallback: OptionId | OptionId[] | undefined,
): boolean {
  if (fallback === undefined) return false;
  if (!Array.isArray(value) && !Array.isArray(fallback)) {
    return value === fallback;
  }

  // A multi-select group is a set: `toggleSelectedOption` appends, so the same
  // selection can arrive in a different order than the configured default.
  const selected = Array.isArray(value) ? value : [value];
  const defaults = Array.isArray(fallback) ? fallback : [fallback];
  return (
    selected.length === defaults.length &&
    [...selected].sort().join() === [...defaults].sort().join()
  );
}

/**
 * The configuration as it travels to another device: the AR hand-off QR code on
 * the desktop toolbar and the one printed on the offer carry the same payload.
 *
 * Only what differs from the config's own defaults is carried. The receiver
 * reconstructs the rest — `setConfig` starts on `getStartupProductId`, the
 * configured default model and `getDefaultSelectedOptions`, and
 * `mergeSharedOptions` overrides only the groups the link names — so an omitted
 * field resolves to exactly the value that produced it.
 *
 * This is not a micro-optimisation: the payload is what fixes the QR code's
 * version, the version fixes how many modules the same printed square holds,
 * and below roughly two device pixels per module a scanner stops reading it.
 * For the built-in configs it takes a typical link from ~165 to ~80 bytes,
 * which is two QR versions and a third more room per module.
 */
export function buildSharedSelection(
  config: ConfiguratorConfig,
  productId: Id,
  modelId: Id,
  selectedOptions: SelectedOptions,
): SharedSelection {
  const defaults = getDefaultSelectedOptions(
    findProduct(config.catalog, productId),
  );

  return {
    productId:
      productId === getStartupProductId(config.catalog) ? undefined : productId,
    modelId: modelId === config.model.defaultModelId ? undefined : modelId,
    selectedOptions: Object.fromEntries(
      Object.entries(selectedOptions).filter(
        ([groupId, value]) => !isSameSelection(value, defaults[groupId]),
      ),
    ),
  };
}
